/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import sortby from 'lodash.sortby';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getSubscriptions} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {CTCEntity, Link} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, Options} from '@ion/dls/src/components/menus/Dropdown';

/**
 * Defines the prop required by this component.
 */
export type SubscriptionChooserProps = {
  accountId: number;
  onChange?: (subscription: CTCEntity) => void;
  readOnly?: boolean;
  required?: boolean;
  includeNone?: boolean
  validationError?: string;
  classes?: string;
  defaultValue?: Link;
  isEditMode?: boolean;
  isViewMode?: boolean;
  isCalampSupport?: boolean;
}

const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * The SubscriptionChooser provides a list of subscriptions for display within a dropdown list
 * allowing the user to select a subscription from the list.
 *
 * ```typescript
 *
 *     import {SubscriptionChooser} from '@ion/features/src/entities/subscription/SubscriptionChooser';
 *
 *     <SubscriptionChooser defaultValue={subscription}
 *        accountId={accountId}
 *        validationError=""
 *        onChange={selectedIOProfileCallback} />
 *
 * ```
 *
 * @accountId        Allows for providing an account id for which the subscription list is fetched.
 * @onChange         Callback prop which will be invoked with the seleted Subscription.
 * @readOnly         When true, the component will be displayed in read-only mode;
 *                   that is, disabled. Defaults to false.
 * @required         When true, the required indicator will be displayed.
 * @validationError  When true, the validationError indicator will be displayed.
 * @classes          Optional classes to apply to the wrapping element.
 * @includeNone      When true (default), localized 'None' option will be displayed.
 * @defaultValue     Default Value of the field
 */
export const SubscriptionChooser = ({
  accountId,
  onChange,
  readOnly,
  required,
  classes,
  validationError,
  defaultValue,
  isViewMode = false,
  isEditMode = false,
  isCalampSupport = false,
  includeNone = true
}: SubscriptionChooserProps) => {
  const _classes                          = useStyles();
  const [subscriptions, setSubscriptions] = useState<CTCEntity[]>(null);
  const [isLoading, setIsLoading]         = useState<boolean>(false);
  const [options, setOptions]             = useState<Options[]>([]);
  const [selected, setSelected]           = useState<number | string>(null);
  const hasMounted                        = useHasMounted();
  const isAddMode                         = !isEditMode && !isViewMode;
  
  // whenever the account changes the subscription are reloaded ...
  useEffect(() => {
    // manages loading Subscriptions when an accountId is provided ...
    const loadSubscriptions = async(accountId: number) => {
      if (isAddMode || (isEditMode && isCalampSupport)) {
        setIsLoading(true);
        try {
          const results = await getSubscriptions(accountId);
          if (results?.length) {
            const {id, name, _self, lowerSubscriptions} = results?.[0];
            const subscriptions    = lowerSubscriptions.map(({title:name, href}) => ({id: entityId(href), name, href}));
            const subscriptionList = sortby([{name, id, href: _self.href}, ...subscriptions], 'name');
            setSubscriptions(subscriptionList);
            setOptions(createOptions({
              items     : subscriptionList,
              valProp   : 'id',
              labelProp : 'name'
            }));
          }
        } catch (err) {
          error(`SubscriptionChooser: Error occurred retrieving Subscriptions for account ${accountId}`);
        } finally {
          setIsLoading(false);
        }
      }
    };

    accountId && loadSubscriptions(accountId);
  }, [accountId, isAddMode, isEditMode, isCalampSupport]);

  // if a default subscription is provided set it as the selected ...
  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue.title);
    }
  }, [defaultValue]);

  /**
   * required getting isEnterprise value. If Enterprise Account is false then
   * set the selected with null.
   */
  useEffect(() => {
    if (!required) {
      setSelected(null);
    }
  }, [required]);

  /**
 * Provides a utility which filters a list of subscriptions for the
 * currently selected id.
 */
  const getSelectedValue = (
    subscriptions: CTCEntity[],
    selected: number | string
  ): CTCEntity => {
    return subscriptions.find(({name}: CTCEntity) => name === selected);
  };

  return (
    <div className={classes || _classes.root}>
      <Dropdown displaySelectedLabel
        includeNone={includeNone}
        required={required}
        disabled={readOnly || isLoading || options?.length === 0}
        hasError={Boolean(validationError)}
        errorText={translate('common:editors.shared.required')}
        value={!isLoading ? selected : translate('common:states.loading')}
        options={options}
        fieldLabel={translate('common:editors.subscription.label')}
        placeholder={translate('common:editors.subscription.placeholder')}
        preferPlaceholder={!isLoading && !selected}
        onChange={onChange ? function({target}) {
          const id = target.value as unknown as number;
          setSelected(id);
          onChange(getSelectedValue(subscriptions, id));
        } : null} />
    </div>
  );
};

export default SubscriptionChooser;
